/**
 * Environment configuration for the CRM app
 */

import { currentPartner } from '@/config/current-partner';

const API_OVERRIDE_KEY = '_tgd_api_override';

/**
 * Runtime API URL override for Vercel preview deployments.
 * Usage: append ?_api=https://tagadapay-git-feat-xxx.vercel.app to point
 * a CRM preview at a specific backend preview. Persisted in sessionStorage.
 * Honoured on *.vercel.app hosts only: anywhere else a link could send the
 * user's API calls, and their auth token, to any host.
 * Use ?_api=clear to remove the override.
 */
function checkApiOverride(): string | null {
    if (typeof window === 'undefined') return null;

    if (!window.location.hostname.endsWith('.vercel.app')) {
        sessionStorage.removeItem(API_OVERRIDE_KEY);
        return null;
    }

    const params = new URLSearchParams(window.location.search);
    const apiParam = params.get('_api');

    if (apiParam === 'clear') {
        sessionStorage.removeItem(API_OVERRIDE_KEY);
        const clean = new URL(window.location.href);
        clean.searchParams.delete('_api');
        window.history.replaceState(null, '', clean.toString());
        return null;
    }

    if (apiParam) {
        const url = apiParam.replace(/\/+$/, '');
        sessionStorage.setItem(API_OVERRIDE_KEY, url);
        const clean = new URL(window.location.href);
        clean.searchParams.delete('_api');
        window.history.replaceState(null, '', clean.toString());
        return url;
    }

    return sessionStorage.getItem(API_OVERRIDE_KEY);
}

const TAGADA_DOMAINS = ['tagada.io', 'tagada.dev', 'tagadapay.com', 'tagadapay.dev'];

/** Shared backend host - partner apps always route API calls here. */
const SHARED_API_ORIGIN = 'https://api.tagada.io';

function getApiBaseUrl(): string {
    const override = checkApiOverride();
    if (override) return override;

    // Explicit env var always wins
    if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
    }

    if (typeof window !== 'undefined') {
        const { hostname, protocol } = window.location;

        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return 'http://app.localhost:3000';
        }

        // Same-origin API when running on a tagada-owned domain
        if (TAGADA_DOMAINS.some(d => hostname.includes(d))) {
            return `${protocol}//${hostname.replace(/:\d+$/, '')}`;
        }

        // Partner whitelabel hosts (injected by the middleware) route to shared backend
        if (currentPartner()) {
            return SHARED_API_ORIGIN;
        }

        // Vercel preview (set VITE_API_URL for a specific backend preview)
        if (hostname.endsWith('.vercel.app')) {
            return SHARED_API_ORIGIN;
        }

        // Unknown https host - fall back to shared backend (safer than
        // accidentally hitting same-origin on an unconfigured deployment)
        if (protocol === 'https:') {
            return SHARED_API_ORIGIN;
        }
    }

    return 'http://localhost:3000';
}

export const config = {
    apiBaseUrl: getApiBaseUrl(),
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
    isPreview: typeof window !== 'undefined' && window.location.hostname.endsWith('.vercel.app'),
    introCallUrl: import.meta.env.VITE_INTRO_CALL_URL ?? '',
    /** payment-stack API (dashboard.tagadapay.io backend), read by the Gleap support tools. */
    paymentStackApiUrl: (import.meta.env.VITE_PAYMENT_STACK_API_URL || 'https://api.tagadapay.io').replace(/\/+$/, ''),
} as const;
